import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatSessionOccupiedError,
  getAllErrorsAsString,
  isSessionOverlapError,
  ServicesURL,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Association } from "src/interfaces/Common";
import { SessionGetInterface } from "@components/EditionFormCst/types";
import { useParams } from "react-router-dom";

export const useAddEditVirtualEdition = (
  triggetEdition: (input: any) => any,
  id?: string,
  dataDetail?: any,
  mutate?: any,
  activityDuration?: number,
  mutateSession?: any,
) => {
  dayjs.extend(advancedFormat);
  const { t } = useTranslation();
  const [openConfirmStatus, setOpenConfirmStatus] = useState<{
    s: StatusEnum;
    message?: string;
  }>();
  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [openAssociationLocation, setOpenAssociationLocation] = useState<
    boolean | Association
  >(false);
  const { idEdition } = useParams();
  const [activitiesAssociation, setActivitiesAssociation] = useState<any>([]);
  const [facultiesAssociation, setFacultiesAssociation] = useState<any>([]);
  const [tutorsAssociation, setTutorsAssociation] = useState<any>([]);
  const [producerAssociation, setProducerAssociation] = useState<any>([]);
  const [presenterAssociation, setPresenterAssociation] = useState<any>([]);
  const [coorganizerAssociation, setCoorganizerAssociation] = useState<any>([]);
  const [cohostAssociation, setCohostAssociation] = useState<any>([]);
  const [hasDifferentDuration, setHasDifferentDuration] = useState(false);
  const [savedInputForm, setSavedInputForm] = useState(null);
  const [savedSessions, setSavedSessions] = useState<SessionGetInterface[]>([]);

  useEffect(() => {
    if (dataDetail) {
      dataDetail?.learning_objects &&
        setActivitiesAssociation([dataDetail.learning_objects]);
      dataDetail?.faculties && setFacultiesAssociation(dataDetail?.faculties);
    }
  }, [dataDetail]);

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "learning_objects":
        setOpenAssociations(
          value
            ? {
                accessorKey: "learning_objects",
                mediaType: "learning_objects",
                type: "table",
                titleKey: "activities-association",
                pageId: PagesID["corporate.catalog.activities"],
                service: ServicesURL.getActivities.replace(
                  ":idCorporate",
                  id || "",
                ),
                multiChoice: false,
                defaultFilters: { type: "DA_VIRTUAL_CLASS" },
                manageConfigs: {
                  type: {
                    optionsToDisable: [
                      "DA_PHYSICAL_CLASS",
                      "DA_EXTERNAL_RES",
                      "DA_YOUTUBE",
                      "DA_LINKEDIN",
                      //"DA_LTI",
                    ],
                  },
                },
              }
            : value,
        );
        break;
      case "faculties":
        setOpenAssociations(
          value
            ? {
                accessorKey: "faculties",
                mediaType: "faculties",
                type: "table",
                titleKey: "faculties-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties + `?corporate_id=${id}&role=TEACHER`,
              }
            : value,
        );
        break;
      case "tutors":
        setOpenAssociations(
          value
            ? {
                accessorKey: "tutors",
                mediaType: "tutors",
                type: "table",
                titleKey: "faculties-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties + `?corporate_id=${id}&role=TUTOR`,
              }
            : value,
        );
        break;
      case "producer":
        setOpenAssociations(
          value
            ? {
                accessorKey: "producer",
                mediaType: "producer",
                type: "table",
                titleKey: "subscribed-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties +
                  `?corporate_id=${id}&not_roles=LEARNER`,
              }
            : value,
        );
        break;
      case "presenter":
        setOpenAssociations(
          value
            ? {
                accessorKey: "presenter",
                mediaType: "presenter",
                type: "table",
                titleKey: "subscribed-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties +
                  `?corporate_id=${id}&not_roles=LEARNER`,
              }
            : value,
        );
        break;
      case "coorganizer":
        setOpenAssociations(
          value
            ? {
                accessorKey: "coorganizer",
                mediaType: "coorganizer",
                type: "table",
                titleKey: "subscribed-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties +
                  `?corporate_id=${id}&not_roles=LEARNER`,
              }
            : value,
        );
        break;
      case "cohost":
        setOpenAssociations(
          value
            ? {
                accessorKey: "cohost",
                mediaType: "cohost",
                type: "table",
                titleKey: "subscribed-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service:
                  ServicesURL.faculties +
                  `?corporate_id=${id}&not_roles=LEARNER`,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "learning_objects":
        return activitiesAssociation;
      case "faculties":
        return facultiesAssociation;
      case "tutors":
        return tutorsAssociation;
      case "producer":
        return producerAssociation;
      case "presenter":
        return presenterAssociation;
      case "coorganizer":
        return coorganizerAssociation;
      case "cohost":
        return cohostAssociation;
    }
  };

  const getDisabled = (accessorKey: string) => {
    switch (accessorKey) {
      case "producer":
        return [
          ...presenterAssociation.map((item: any) => Number(item.id)),
          ...coorganizerAssociation.map((item: any) => Number(item.id)),
        ];
      case "presenter":
        return [
          ...producerAssociation.map((item: any) => Number(item.id)),
          ...coorganizerAssociation.map((item: any) => Number(item.id)),
        ];
      case "coorganizer":
        return [
          ...producerAssociation.map((item: any) => Number(item.id)),
          ...presenterAssociation.map((item: any) => Number(item.id)),
        ];
      default:
        return [];
    }
  };

  const resetSessionAssociations = () => {
    setFacultiesAssociation([]);
    setTutorsAssociation([]);
    setProducerAssociation([]);
    setPresenterAssociation([]);
    setCoorganizerAssociation([]);
    setCohostAssociation([]);
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "learning_objects":
        setActivitiesAssociation(
          activitiesAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "faculties":
        setFacultiesAssociation(
          facultiesAssociation.filter((element: any) => element.id !== e.id),
        );

        setPresenterAssociation(
          presenterAssociation.filter((element: any) => element.id !== e.id),
        );

        setCohostAssociation(
          cohostAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "tutors":
        setTutorsAssociation(
          tutorsAssociation.filter((element: any) => element.id !== e.id),
        );

        setPresenterAssociation(
          presenterAssociation.filter((element: any) => element.id !== e.id),
        );

        setCohostAssociation(
          cohostAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "producer":
        setProducerAssociation(
          producerAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "presenter":
        setPresenterAssociation(
          presenterAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "coorganizer":
        setCoorganizerAssociation(
          coorganizerAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "cohost":
        setCohostAssociation(
          cohostAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "learning_objects":
        const objToActivitiesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return activitiesAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setActivitiesAssociation(objToActivitiesAssociation);
        break;
      case "faculties":
        const objToFacultiesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return facultiesAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setFacultiesAssociation(objToFacultiesAssociation);

        // Aggiungi in presenterAssociation tutti gli oggetti presenti
        // in objToFacultiesAssociation ma non già presenti in presenterAssociation
        const presenterIds = (presenterAssociation ?? []).map((item: any) =>
          String(item?.id),
        );
        const toAddToPresenter = (objToFacultiesAssociation ?? []).filter(
          (item: any) =>
            !presenterIds.includes(String(item?.id)) &&
            !(cohostAssociation ?? []).some(
              (cohost: any) => String(cohost?.id) === String(item?.id),
            ),
        );
        setPresenterAssociation([
          ...(presenterAssociation ?? []),
          ...toAddToPresenter,
        ]);
        setCohostAssociation([
          ...(cohostAssociation ?? []),
          ...toAddToPresenter,
        ]);

        break;
      case "tutors":
        const objToTutorsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return tutorsAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setTutorsAssociation(objToTutorsAssociation);

        // Aggiungi in presenterAssociation tutti gli oggetti presenti in objToTutorsAssociation
        // ma non già presenti in presenterAssociation
        const safePresenterAssociation = Array.isArray(presenterAssociation)
          ? presenterAssociation
          : [];
        const safeCohostAssociation = Array.isArray(cohostAssociation)
          ? cohostAssociation
          : [];
        const safeObjToTutorsAssociation = Array.isArray(objToTutorsAssociation)
          ? objToTutorsAssociation
          : [];

        const presenterIds2 = safePresenterAssociation.map((item: any) =>
          String(item.id),
        );
        const toAddToPresenter2 = safeObjToTutorsAssociation.filter(
          (item: any) =>
            !presenterIds2.includes(String(item.id)) &&
            !safeCohostAssociation.some(
              (cohost: any) => String(cohost.id) === String(item.id),
            ),
        );
        setPresenterAssociation([
          ...safePresenterAssociation,
          ...toAddToPresenter2,
        ]);
        setCohostAssociation([...safeCohostAssociation, ...toAddToPresenter2]);

        break;
      case "producer":
        const objToProducerAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return producerAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setProducerAssociation(objToProducerAssociation);
        break;
      case "presenter":
        const objToPresenterAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return presenterAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setPresenterAssociation(objToPresenterAssociation);
        break;
      case "coorganizer":
        const objToCooranizerAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return coorganizerAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setCoorganizerAssociation(objToCooranizerAssociation);
        break;
      case "cohost":
        const objToCohostAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return cohostAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setCohostAssociation(objToCohostAssociation);
        break;
    }
  };

  // Helper per convertire array di oggetti con id in array di numeri
  const mapToIds = (items?: { id: string }[]): number[] => {
    return items ? items.map((item) => Number(item.id)) : [];
  };

  // Helpers per la creazione dei ruoli
  const buildMsTeamsRoles = (session: SessionGetInterface) => {
    const producerIds = mapToIds(session.producer);
    const coorganizerIds = mapToIds(session.coorganizer);
    const presenterIds = mapToIds(session.presenter);

    return {
      PRODUCER: producerIds,
      PRESENTER: presenterIds,
      COORGANIZER: coorganizerIds,
    };
  };

  const buildGmeetRoles = (session: SessionGetInterface) => {
    const cohostIds = mapToIds(session.cohost);

    return {
      COHOST: cohostIds,
    };
  };

  // Helpers per le impostazioni della lobby
  const buildLobbyMsTeamsSettings = (session: SessionGetInterface) => ({
    is_dial_in_bypass_enabled: session.is_dial_in_bypass_enabled || false,
    scope: session.scope || null,
  });

  const buildLobbyGmeetSettings = (session: SessionGetInterface) => ({
    access_type: session.access_type || null,
  });

  // Helper per costruire le impostazioni per MSTEAMS
  const buildMsTeamsSettings = (session: SessionGetInterface) => ({
    roles: buildMsTeamsRoles(session),
    settings: {
      lobby_settings: buildLobbyMsTeamsSettings(session),
      allow_attendee_to_enable_camera:
        session.allow_attendee_to_enable_camera || false,
      allow_attendee_to_enable_mic:
        session.allow_attendee_to_enable_mic || false,
      allow_breakout_rooms: session.allow_breakout_rooms || false,
      allow_meeting_chat: session.allow_meeting_chat || null,
      allow_recording: session.allow_recording || false,
      allow_transcription: session.allow_transcription || false,
      record_automatically: session.record_automatically || false,
      allowed_presenters: session.allowed_presenters || null,
    },
  });

  // Helper per costruire le impostazioni per GMEET
  const buildGmeetSettings = (session: SessionGetInterface) => ({
    roles: buildGmeetRoles(session),
    settings: {
      lobby_settings: buildLobbyGmeetSettings(session),
      chat_restriction: session.chat_restriction || null,
      default_join_as_viewer_type: session.default_join_as_viewer_type || false,
      auto_transcription_generation:
        session.auto_transcription_generation || false,
      auto_recording_generation: session.auto_recording_generation || false,
    },
  });

  // Helper per costruire le impostazioni per OTHER
  const buildOtherSettings = (session: SessionGetInterface) => ({
    settings: {
      link: session.link || null,
    },
  });

  const onSubmit = async (
    inputForm: any,
    sessions: SessionGetInterface[],
    isDiversityConfirmation: boolean = false,
  ) => {
    const loId =
      activitiesAssociation.length > 0
        ? parseInt(activitiesAssociation[0].id)
        : activitiesAssociation;

    const newInputForm = {
      lo_id: loId,
      edition_code: inputForm.edition_code || null,
      edition_name: inputForm.edition_name || null,
      extra_info: inputForm.extra_info ?? inputForm.extraInfo ?? null,
      publish_date: dayjs(inputForm.publish_date).format("YYYY-MM-DD"),
      //expected_completion_time: inputForm.expected_completion_time || 0,
      sessions: sessions.map((session) => {
        const { tool } = session;

        const baseSession = {
          id: session.id || null,
          //publish_date_session: dayjs(session.publish_date_session).format("YYYY-MM-DD"),
          start_date: dayjs(session.start_date).format("YYYY-MM-DDTHH:mm:ssZ"),
          end_date: dayjs(session.end_date).format("YYYY-MM-DDTHH:mm:ssZ"),
          faculties: mapToIds(session.faculties),
          tutors: mapToIds(session.tutors),
          //"QR-code": false,
          online_meeting: {
            tool: tool || null,
            trigger_external_services:
              (session.trigger_external_services &&
                dayjs().isAfter(new Date(inputForm.publish_date).getTime())) ||
              !!session.online_meeting_updated_at ||
              false,
            confirmed:
              (session.confirmed &&
                (session.trigger_external_services || tool === "OTHER")) ||
              false,
          },
        };

        if (tool === "MSTEAMS") {
          baseSession.online_meeting = {
            ...baseSession.online_meeting,
            ...buildMsTeamsSettings(session),
          };
        } else if (tool === "GMEET") {
          baseSession.online_meeting = {
            ...baseSession.online_meeting,
            ...buildGmeetSettings(session),
          };
        } else if (tool === "OTHER") {
          baseSession.online_meeting = {
            ...baseSession.online_meeting,
            ...buildOtherSettings(session),
          };
        }

        return baseSession;
      }),
    };

    const sessionsDuration = sessions.reduce((total, obj) => {
      const start: any = new Date(obj.start_date);
      const end: any = new Date(obj.end_date);
      const duration = (end - start) / (1000 * 60);
      return total + Math.abs(duration);
    }, 0);

    if (
      !isDiversityConfirmation &&
      activityDuration &&
      sessionsDuration != activityDuration
    ) {
      setHasDifferentDuration(true);
      setSavedInputForm(inputForm);
      setSavedSessions(sessions);
      return;
    }

    setHasDifferentDuration(false);
    setSavedInputForm(null);
    setSavedSessions([]);

    setOpenConfirmStatus({ s: StatusEnum.LOADING });

    try {
      await triggetEdition(newInputForm);
      await Promise.all([mutate && mutate(), mutateSession && mutateSession()]);
      setOpenConfirmStatus({ s: StatusEnum.OK });
    } catch (error) {
      const errorMessage = isSessionOverlapError(error)
        ? formatSessionOccupiedError(error, t)
        : getAllErrorsAsString(error);
      setOpenConfirmStatus({
        s: StatusEnum.KO,
        message: errorMessage,
      });
    }
  };

  return {
    onSubmit,
    setOpenConfirmStatus,
    openConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    getDisabled,
    resetSessionAssociations,
    openAssociations,
    openAssociationLocation,
    facultiesAssociation,
    setFacultiesAssociation,
    tutorsAssociation,
    producerAssociation,
    presenterAssociation,
    coorganizerAssociation,
    cohostAssociation,
    setProducerAssociation,
    setPresenterAssociation,
    setCoorganizerAssociation,
    setCohostAssociation,
    setTutorsAssociation,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  };
};
